import { useRef, useCallback } from "react";

export function useThrottle<T extends (...args: any[]) => any>(
  fn: T,
  delay: number,
): T {
  const lastRun = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastArgs = useRef<Parameters<T>>(null);

  return useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now();

      if (now - lastRun.current >= delay) {
        fn(...args);
        lastRun.current = now;

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      } else if (!timeoutRef.current) {
        timeoutRef.current = setTimeout(
          () => {
            const remaining = delay - (Date.now() - lastRun.current);
            if (remaining <= 0 && lastArgs.current) {
              fn(...lastArgs.current);
              lastRun.current = Date.now();
            }
            timeoutRef.current = null;
          },
          delay - (now - lastRun.current),
        );
      }

      lastArgs.current = args;
    },
    [fn, delay],
  ) as T;
}
